import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Dict, Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';
import { strings } from '/imports/strings';

export type ProjectActivityType =
  | 'created'
  | 'deleted'
  | 'assigneeChanged'
  | 'statusChanged'
  | 'fieldsEdited'
  | 'enrichmentStarted'
  | 'enrichmentCompleted'
  | 'enrichmentFailed'
  | 'enrichmentStep'
  | 'documentAdded'
  | 'documentApproved'
  | 'documentDeleted'
  | 'quoteCreated'
  | 'quoteUpdated'
  | 'quoteDeleted'
  | 'consumptionMetersApplied'
  | 'hubspotPortalRecordCreated'
  | 'epcPriceModelRun';

export type ProjectActivityActor = 'user' | 'system' | 'job';

export type ProjectActivityCategory = 'lifecycle' | 'enrichment' | 'documents' | 'edits' | 'integrations';

export const ProjectActivityCategoryByType: Dict<ProjectActivityCategory> = {
  'created': 'lifecycle',
  'deleted': 'lifecycle',
  'assigneeChanged': 'lifecycle',
  'statusChanged': 'lifecycle',
  'fieldsEdited': 'edits',
  'enrichmentStarted': 'enrichment',
  'enrichmentCompleted': 'enrichment',
  'enrichmentFailed': 'enrichment',
  'enrichmentStep': 'enrichment',
  'documentAdded': 'documents',
  'documentApproved': 'documents',
  'documentDeleted': 'documents',
  'quoteCreated': 'lifecycle',
  'quoteUpdated': 'lifecycle',
  'quoteDeleted': 'lifecycle',
  'consumptionMetersApplied': 'lifecycle',
  'hubspotPortalRecordCreated': 'integrations',
  'epcPriceModelRun': 'integrations',
};

export interface ProjectActivity extends Doc {
  projectId: string;
  orgId: string;
  type: ProjectActivityType;
  actor: ProjectActivityActor;
  actorAccountId?: string;
  actorName?: string;
  data?: Dict<any>;
}

export type Model = ProjectActivity;

export const ProjectActivities = new Mongo.Collection<ProjectActivity>('projectActivities');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: ProjectActivities,
  isOrgOwned: true,
  editableFields: [],
  listMethod: DeclareMethod<ListMethodArg<ProjectActivity>, ListMethodResponse<ProjectActivity, Refs>>('listProjectActivities'),
  getMethod: DeclareMethod<string, GetMethodResponse<ProjectActivity, Refs>>('getProjectActivity'),
  createMethod: DeclareMethod<Partial<ProjectActivity>, string>('createProjectActivity'),
  updateMethod: DeclareMethod<Partial<ProjectActivity>, string>('updateProjectActivity'),
  deleteMethod: DeclareMethod<string>('deleteProjectActivity'),
  pathPrefix: '/project-activities',
  asString: (document) => document.type,
  modelName: strings.Activity(),
  pluralName: strings.Activity(),
  validate: async (activity: Partial<ProjectActivity>) => {
    const errors: { [key: string]: string[] } = {};
    validateString(errors, activity, 'projectId', { required: true });
    validateString(errors, activity, 'type', { required: true });
    return errors;
  },
};

Meteor.isServer && Meteor.startup(async () => {
  await ProjectActivities.createIndexAsync({ projectId: 1, createdAt: -1 });
  await ProjectActivities.createIndexAsync({ orgId: 1, createdAt: -1 });
  await ProjectActivities.createIndexAsync({ type: 1 });
});
